import { BrokenImage } from "@mui/icons-material";
import { Avatar, Box, Typography } from "@mui/material";
import { useMemo, useState } from "react";

type SmartLogoProps = {
  logoUrl?: string | null;
  name: string;
};

const LOGO_SIZE = 40;

const SmartLogo: React.FC<SmartLogoProps> = ({ logoUrl, name }) => {
  const [hasImageError, setHasImageError] = useState(false);

  const showImage = useMemo(() => Boolean(logoUrl) && !hasImageError, [logoUrl, hasImageError]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `${LOGO_SIZE}px 1fr`,
        columnGap: 2,
        alignItems: "center",
        width: "100%",
        minWidth: 0,
        overflow: "visible",
      }}
    >
      <Box
        sx={{
          width: LOGO_SIZE,
          height: LOGO_SIZE,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "visible",
          position: "relative",
          zIndex: 0,
          "& img": {
            transform: "scale(1)",
            transition: "transform 180ms ease-in-out",
            transformOrigin: "center center",
            willChange: "transform",
          },
          "&:hover": {
            zIndex: 10,
          },
          "&:hover img": {
            transform: "scale(8)",
          },
        }}
      >
        {showImage ? (
          <Box
            component="img"
            src={logoUrl as string}
            alt={name}
            onError={() => setHasImageError(true)}
            sx={{
              width: LOGO_SIZE,
              height: LOGO_SIZE,
              objectFit: "contain",
              borderRadius: "4px",
              display: "block",
            }}
          />
        ) : (
          <Avatar sx={{ width: LOGO_SIZE, height: LOGO_SIZE }} variant="rounded">
            <BrokenImage />
          </Avatar>
        )}
      </Box>

      <Typography
        variant="body1"
        sx={{
          fontWeight: "medium",
          textAlign: "left",
          justifySelf: "start",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={name}
      >
        {name}
      </Typography>
    </Box>
  );
};

export default SmartLogo;